import React, { useEffect, useState } from 'react';
import { 
  Check, X, Info, ScanEye, User, Mail, Phone, MapPin, 
  Clock, FileText, Landmark, ShieldAlert, AlertCircle 
} from 'lucide-react';
import styles from './GuarantorApproval.module.css';
import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import Swal from "sweetalert2";
import { BASE_URL } from '../../Components/Api/Api';
import axios from 'axios';

export default function GuarantorApproval() {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Modal State Control
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' or 'disapprove'
  const [comment, setComment] = useState('');

  // Fetch pending guarantor requests on mount
  useEffect(() => {
    fetchGuarantorRequests();
  }, []);

  const fetchGuarantorRequests = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("token");
    const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");
    try {
      const res = await axios.get(`${BASE_URL}/member-auth/get_guarantor_requests?company_id=${compid}&member_id=${memberid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data && Array.isArray(res.data.data)) {
        // Filter out requests that have already been actioned if backend doesn't filter
        const pendingRequests = res.data.data.filter(req => req.is_actioned === 0);
        setRequests(pendingRequests);
      } else {
        setRequests([]);
      }
    } catch (err) {
      console.error("Failed fetching guarantor requests:", err);
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailModal(true);
  };

  const handleOpenAction = (type) => {
    setActionType(type);
    setShowDetailModal(false);
    setShowActionModal(true);
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) {
      Swal.fire({ icon: 'warning', title: 'Required', text: 'Please enter a review comment.' });
      return;
    }

    setIsSubmitting(true);
    const token = localStorage.getItem("token");
    const endpoint = actionType === 'approve' 
      ? 'approve-guarantor-request' 
      : 'disapprove-guarantor-request';
    const compid = localStorage.getItem("companyId");
    const memberid = localStorage.getItem("memberId");

    try {
      await axios.post(`${BASE_URL}/member-auth/${endpoint}company_id=${compid}&member_id=${memberid}`, {
        guarantor_request_id: selectedRequest.id,
        comment: comment
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      

      Swal.fire({
        icon: "success",
        title: actionType === 'approve' ? "Approved!" : "Declined",
        text: `Guarantor request has been processed successfully.`,
        confirmButtonColor: "#15803d",
      });

      // Cleanup states and refresh list
      setShowActionModal(false);
      setSelectedRequest(null);
      setComment('');
      fetchGuarantorRequests();
      window.dispatchEvent(new Event("refreshGuarantorMenu"));
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "An unexpected system error occurred.";
      Swal.fire({ icon: "error", title: "Action Failed", text: msg });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.container}>
      <Sidebar2 />

      <main className={styles.mainContent}>
        <Header2 />

        <div className={styles.workspace}>
          {/* Top Info Banner Section */}
      

          {isLoading ? (
            <div className={styles.cardOne} style={{ alignItems: 'center', padding: '3rem' }}>
              <div className={styles.radialDial}>
                <div className={styles.dialText}>Loading...</div>
              </div>
            </div>
          ) : requests.length === 0 ? (
            /* Custom center illustration when no data exists */
            <div className={styles.cardOne} style={{ alignItems: 'center', textAlign: 'center', padding: '4rem 2rem' }}>
              <div className={styles.iconWrapper} style={{ backgroundColor: '#f0fdf4', color: '#15803d', width: '80px', height: '80px', borderRadius: '50%' }}>
                <Check size={40} />
              </div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginTop: '1.5rem', color: '#1e293b' }}>All Done!</h2>
              <p style={{ color: '#64748b', fontSize: '0.85rem', maxWidth: '400px', margin: '0.5rem 0 0 0' }}>
                There are no pending guarantor workflow requests requiring your approval.
              </p>
            </div>
          ) : (
            /* Layout Grid List of Requests awaiting action */
            <div className={styles.loanList}>
              {requests.map((req) => (
                <div key={req.id} className={styles.guarantorBox} style={{ backgroundColor: '#ffffff' }}>
                  <div className={styles.guarantorHeader}>
                    <div>
                      <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>{req.member_name}</h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#64748b' }}>
                        Member ID: {req.member_number}
                      </p>
                    </div>
                    <span className={styles.requiredBadge} style={{ backgroundColor: '#fcf8f4', color: '#d8811d', border: 'none' }}>
                      Pending Approval
                    </span>
                  </div>

                  <div className={styles.twoColForm} style={{ marginBottom: 0 }}>
                    <div className={styles.formGroup}>
                      <span className={styles.label} style={{ color: '#94a3b8' }}>Loan Type Applied</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{req.loan?.loan_type?.loan_name || req.loan?.loan_name || 'N/A'}</span>
                    </div>
                  
                    <div className={styles.formGroup}>
                      <span className={styles.label} style={{ color: '#94a3b8' }}>Principal Amount</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#15803d' }}>
                        ₦{parseFloat(req.loan?.principal_amount || 0).toLocaleString()}
                      </span>
                    </div>

                      <div className={styles.formGroup}>
                      <span className={styles.label} style={{ color: '#94a3b8' }}>Total Repayment Amount</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#15803d' }}>
                        ₦{parseFloat(req.loan?.total_repayment || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                    <button 
                      type="button" 
                      title='Click here to review before approvals'
                      className={styles.addGuarantorBtn}
                      onClick={() => handleOpenDetails(req)}
                      style={{ padding: '0.5rem 1rem', fontSize: '0.8rem' }}
                    >
                      <ScanEye size={16} /> <span>Review Loan Data</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* MODAL 1: VIEW COMPREHENSIVE REQUEST DATA FILE */}
      {showDetailModal && selectedRequest && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div className={styles.cardOne} style={{ maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto', borderRadius: '12px', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, margin: 0 }}>Application Guarantee Profile</h3>
              <button onClick={() => setShowDetailModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: '1.5rem' }}>
             <div style={{ backgroundColor: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', textTransform: 'uppercase', color: '#64748b', letterSpacing: '0.05em' }}>Applicant Information</h4>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
    
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
        <User size={16} /> <span>Name</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.member_name}</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
        <FileText size={16} /> <span>Member No.</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.member_number}</strong>
    </div>

    {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
        <Mail size={16} /> <span>Email Address</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b', wordBreak: 'break-all' }}>{selectedRequest.email}</strong>
    </div> */}

    {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
        <Phone size={16} /> <span>Phone Line</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.phone_number}</strong>
    </div> */}

    {/* <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b', flexShrink: 0 }}>
        <MapPin size={16} /> <span>Residential Add.</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b', maxWidth: '60%' }}>{selectedRequest.address}</strong>
    </div> */}

  </div>
</div>

<div style={{ backgroundColor: '#f0fdf4', padding: '1rem', borderRadius: '8px', border: '1px solid #dcfce7' }}>
  <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', textTransform: 'uppercase', color: '#166534', letterSpacing: '0.05em' }}>Loan Application Details</h4>
  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
        <Landmark size={16} /> <span>Loan Type</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.loan?.loan_type?.loan_name || selectedRequest.loan?.loan_name || 'N/A'}</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
        <Clock size={16} /> <span>Loan Purpose</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.loan?.loan_purpose || 'N/A'}</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
        <Clock size={16} /> <span>Duration of Payments</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b' }}>{selectedRequest.loan?.duration || 'N/A'} months</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
        <FileText size={16} /> <span>Principal Amount</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#15803d' }}>₦{parseFloat(selectedRequest.loan?.principal_amount || 0).toLocaleString()}</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534' }}>
        <FileText size={16} /> <span>Interest Amount</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#ea580c' }}>₦{parseFloat(selectedRequest.loan?.interest_amount || 0).toLocaleString()}</strong>
    </div>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', borderTop: '1px dashed #cbd5e1', paddingTop: '0.5rem', marginTop: '0.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#166534', fontWeight: '600' }}>
        <FileText size={16} /> <span>Total Repayment Amount</span>
      </div>
      <strong style={{ textAlign: 'right', color: '#1e293b', fontSize: '0.9rem' }}>₦{parseFloat(selectedRequest.loan?.total_repayment || 0).toLocaleString()}</strong>
    </div>

  </div>
</div>
              <div className={styles.infoBox} style={{ marginTop: '0.5rem' }}>
                <ShieldAlert size={24} style={{ color: '#15803d', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 600 }}>Liability Warning</p>
                  <p style={{ margin: '2px 0 0 0', fontSize: '0.7rem', color: '#475569' }}>By approving this request, you agree to fulfill secondary liability provisions mapping out this loan setup asset rule.</p>
                </div>
              </div>
            </div>

            {/* Bottom Actions Frame */}
            <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
              <button 
                type="button" 
                className={styles.removeGuarantorBtn}
                onClick={() => handleOpenAction('disapprove')}
                style={{ flex: 1, padding: '0.75rem', justifyContent: 'center' }}
              >
                <X size={16} /> <span>Decline Request</span>
              </button>
              <button 
                type="button" 
                className={styles.addGuarantorBtn}
                onClick={() => handleOpenAction('approve')}
                style={{ flex: 1, padding: '0.75rem', justifyContent: 'center', alignItems: 'center' }}
              >
                <Check size={16} /> <span>Accept & Approve</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 2: INTERMEDIARY WORKFLOW COMMENT INPUT MODAL */}
      {showActionModal && selectedRequest && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '1rem' }}>
          <div className={styles.cardOne} style={{ maxWidth: '450px', width: '100%', borderRadius: '12px', padding: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', paddingBottom: '1rem' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                {actionType === 'approve' ? 'Approve' : 'Reject'} Request Comment
              </h3>
              <button onClick={() => { setShowActionModal(false); setShowDetailModal(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={20} /></button>
            </div>

            <form onSubmit={handleActionSubmit} style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div className={styles.formGroup}>
                <label className={styles.label} htmlFor="modalComment">
                  Workflow Review Notes <span>*</span>
                </label>
                <textarea
                  id="modalComment"
                  className={styles.input}
                  style={{ height: '100px', padding: '0.5rem', resize: 'none' }}
                  placeholder="Enter specific audit observations, verification context, or justification remarks here..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem' }}>
                <button 
                  type="button" 
                  className={styles.backBtn}
                  onClick={() => { setShowActionModal(false); setShowDetailModal(true); }}
                  style={{ flex: 1, padding: '0.6rem', justifyContent: 'center', alignItems: 'center' }}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className={styles.nextBtn}
                  style={{ flex: 1, padding: '0.6rem', backgroundColor: actionType === 'approve' ? '#15803d' : '#ef4444', justifyContent: 'center', alignItems: 'center' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Dispatching...' : 'Submit Action'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}